/**
 * iter-97 — Auto-detect frontend errors → Support Bot toast.
 *
 * Hooks into the existing `consoleTail` install path (so the buffer is
 * filled) AND independently listens for:
 *   - window.onerror (uncaught script errors)
 *   - window.onunhandledrejection (uncaught Promise rejections)
 *   - audio NotAllowedError events from playbackUnlock.safePlay()
 *
 * When a recognised pattern fires, surfaces a one-line Sonner toast with
 * an action button that either:
 *   - Applies the fix client-side (e.g. installPlaybackUnlock()),
 *   - Or opens the Support Bot pre-loaded with the error context so the
 *     user just hits Send.
 *
 * Designed to be conservative: dedupes within a sliding 60-second window,
 * never fires more than once per error signature in that window. Never
 * touches the user's existing error reporting — purely additive UX.
 */
import { toast } from "sonner";
import { installPlaybackUnlock } from "@/lib/playbackUnlock";

const SUPPRESS_WINDOW_MS = 60_000;
const _recentlyFired = new Map(); // signature → expiresAt

let installed = false;

/** Patterns we know how to fix automatically. Each entry has a
 *  detect(text) → boolean and a remediation that returns a toast spec. */
const PATTERNS = [
  {
    id: "audio-not-allowed",
    detect: (txt) => /NotAllowedError|user\s*didn'?t\s*interact|play\(\)\s*failed/i.test(txt),
    remediate: () => ({
      title: "Tap once to unlock playback",
      description: "Your browser blocked autoplay — tapping the page fixes it instantly.",
      action: {
        label: "Tap now",
        onClick: () => installPlaybackUnlock(),
      },
    }),
  },
  {
    id: "audio-context-suspended",
    detect: (txt) => /AudioContext was not allowed to start/i.test(txt),
    // This is a Chrome warning emitted automatically on page load; we don't
    // need to surface a toast — installPlaybackUnlock already arms it on
    // the first user gesture. Return null to silently dedupe.
    remediate: () => null,
  },
  {
    id: "network-failed",
    detect: (txt) => /(Failed to fetch|NetworkError|ECONNREFUSED|net::ERR_)/i.test(txt),
    remediate: (txt) => ({
      title: "Network hiccup",
      description: "Couldn't reach the server — your connection may have dropped briefly. Try again in a moment.",
      action: {
        label: "Help",
        onClick: () => openSupportBotWith("My network keeps failing"),
      },
      _err: txt.slice(0, 200),
    }),
  },
  {
    id: "rate-limit",
    detect: (txt) => /Rate limit exceeded|429/i.test(txt),
    remediate: () => ({
      title: "Rate limit hit",
      description: "Slow down a beat — try again in a minute. Owners aren't rate-limited.",
    }),
  },
];

function openSupportBotWith(message) {
  try {
    window.dispatchEvent(new CustomEvent("elpm:open-support-bot", { detail: { message } }));
  } catch (e) { void e; }
}

function fireIfNew(text) {
  if (!text) return;
  for (const p of PATTERNS) {
    if (!p.detect(text)) continue;
    const now = Date.now();
    const exp = _recentlyFired.get(p.id);
    if (exp && exp > now) return;  // suppress dup within 60 s
    _recentlyFired.set(p.id, now + SUPPRESS_WINDOW_MS);
    const spec = p.remediate(text);
    if (!spec) return;             // silent dedupe (e.g. audio-context-suspended)
    toast.error(spec.title, {
      description: spec.description,
      action: spec.action,
      duration: 8000,
    });
    return;
  }
}

/** Install the global listeners. Idempotent — safe to call from app boot. */
export function installErrorAutoDetect() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  // Patch console.error so existing tail-buffer logic still works AND we
  // get a hook on every error message. We use console.error rather than
  // window.onerror because most React/axios errors only surface through
  // console.error (not as uncaught exceptions).
  const origErr = console.error.bind(console);
  console.error = (...args) => {
    try { fireIfNew(args.map((a) => (typeof a === "string" ? a : JSON.stringify(a))).join(" ").slice(0, 600)); } catch (e) { void e; }
    return origErr(...args);
  };

  // Native uncaught exceptions + rejections.
  window.addEventListener("error", (e) => {
    const txt = `${e?.message || ""} ${e?.filename || ""}`;
    fireIfNew(txt);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e?.reason;
    const txt = typeof reason === "string" ? reason : (reason?.message || JSON.stringify(reason || {}));
    fireIfNew(txt);
  });
}
